import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { Lock } from '@openx/components-core/lib/Lock/Lock';
import { HeaderActions } from '@openx/components-header/HeaderActions/HeaderActions';
import { PageHeader } from '@openx/components-header/PageHeader';
import { OPENX_AGENT_TOOL } from '@openx/components-openx-agent/components/Home/utils/consts';
import { OpenXAgentIcon } from '@openx/components-openx-agent/components/icons/OpenXAgentIcon';
import { useOpenXAgentContext } from '@openx/components-openx-agent/context/useOpenXAgentContext';
import { PackageDetailsTab } from '@openx/components-package/components/PackageDetails/PackageDetailsTab';
import { packageToFormState } from '@openx/components-package/components/PackageForm/utils/formStateConverter';
import { type PackageRouteParams, PackageRowActionType } from '@openx/components-package/types';
import { usePackageContext } from '@openx/components-package/utils/context/PackageContext';
import { usePackageActionPermissions } from '@openx/components-package/utils/hooks/usePackageActionPermissions';
import { usePackageDeletePermissions } from '@openx/components-package/utils/hooks/usePackageDeletePermissions';
import { usePackageOperations } from '@openx/components-package/utils/hooks/usePackageOperations';
import { ACL_TYPE } from '@openx/types/aclType';
import type { PackageListItem } from '@openx/types/package';
import type { UsePackageUsedByParam } from '@openx/types/package/PackageHookTypes';
import { noop } from '@openx/utils/noop';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useIsAllowed } from '../../../configs/permission/utils/useIsAllowed';

const DetailsTabs = {
  DETAILS: 'details',
} as const;

export const PackageDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { packageId } = useParams<PackageRouteParams>();
  const { callPackageAction, loadingAction } = usePackageOperations();
  const { usePackageFetch, usePackageUsedBy, isOaSuborg } = usePackageContext();
  const isCuratedRoute = searchParams.get('is_curated') === 'true';
  const { setIsOpenXAgentOpen, setActiveTool } = useOpenXAgentContext();
  const { pckgLoading, pckg } = usePackageFetch(packageId as string);
  const { isAllowed, accessUser } = useIsAllowed();
  const { canEdit: canEditBase, canDelete: canDeleteBase } = usePackageActionPermissions({ isAllowed, accessUser });

  const canEdit = useCallback(
    (pkg: PackageListItem) => {
      if (isOaSuborg && pkg?.is_curated) {
        return false; // OA suborganizations cannot edit curated packages
      }
      return canEditBase(pkg);
    },
    [canEditBase, isOaSuborg]
  );

  const canDelete = useCallback(
    (pkg: PackageListItem) => {
      if (isOaSuborg && pkg?.is_curated) {
        return false; // OA suborganizations cannot delete curated packages
      }
      return canDeleteBase(pkg);
    },
    [canDeleteBase, isOaSuborg]
  );

  const packageState = useMemo(() => packageToFormState(pckg || {}), [pckg]);
  const canNavigateToCurated = isCuratedRoute;
  const goToListPage = useCallback(
    () => (canNavigateToCurated ? navigate('/inventory/packages/curated') : navigate('/inventory/packages')),
    [canNavigateToCurated, navigate]
  );

  const packageUsedByParam: UsePackageUsedByParam = useMemo(() => {
    return {
      filters: {
        objects: ['deal', 'floorrule'],
        package_uid: [packageId as string],
      },
    };
  }, [packageId]);

  const packageUsedBy = usePackageUsedBy(packageUsedByParam);

  const { getDeleteActionPermissions } = usePackageDeletePermissions();

  const deleteActionPermissions = useMemo(() => {
    if (!pckg) {
      return;
    }

    return getDeleteActionPermissions({
      pckg,
      permissionCheck: () => canDelete(pckg),
      packageUsedBy,
      notAllowedMsgDefault: '',
    });
  }, [getDeleteActionPermissions, packageUsedBy, pckg, canDelete]);

  const editNotAllowedMessage = useMemo(() => {
    if (!pckg || canEdit(pckg)) {
      return undefined;
    }

    return 'OpenX Curator permissions are required to edit Curated Packages directly.';
  }, [pckg, canEdit]);

  const renderActions = useCallback(
    () => (
      <HeaderActions
        actions={[
          {
            'data-test': 'check-targeting',
            label: t('Check Targeting'),
            onClick: () => {
              setActiveTool({
                toolId: OPENX_AGENT_TOOL.TARGETING_EXPLAINER,
                toolData: pckg,
              });
              setIsOpenXAgentOpen(true);
            },
            startIcon: (
              <Box sx={{ mb: 0.25 }}>
                <OpenXAgentIcon width={16} height={16} />
              </Box>
            ),
            isAllowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED)),
            isHidden: !isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED)),
            color: 'default',
            sx: {
              background: (theme: Theme) =>
                `linear-gradient(310.6deg, ${theme.palette.primary.main} 6.51%, ${theme.palette.background.yellowLight} 87.85%)`,
            },
            showAsActionButton: true,
          },
          {
            color: 'default',
            'data-test': 'edit',
            isAllowed: pckg ? canEdit(pckg) : false,
            label: t('Edit'),
            notAllowedMessage: editNotAllowedMessage,
            onClick: () => navigate(`/inventory/packages/${packageId}/edit`, { state: { ref: location.pathname } }),
            showAsActionButton: true,
          },
          {
            color: 'error',
            'data-test': 'delete',
            isAllowed: !!deleteActionPermissions?.isAllowed,
            label: t('Delete'),
            loading: loadingAction === PackageRowActionType.DELETE || packageUsedBy.isLoading,
            notAllowedMessage: deleteActionPermissions?.notAllowedMessage,
            onClick: () => {
              callPackageAction({
                action: PackageRowActionType.DELETE,
                onFinish: () => goToListPage(),
                packages: [pckg as PackageListItem],
              });
            },
            showAsActionButton: true,
          },
        ]}
      />
    ),
    [
      callPackageAction,
      goToListPage,
      loadingAction,
      location.pathname,
      navigate,
      packageId,
      pckg,
      packageUsedBy,
      deleteActionPermissions,
      editNotAllowedMessage,
      canEdit,
      t,
      setIsOpenXAgentOpen,
      setActiveTool,
      isAllowed,
    ]
  );

  const PreTitleIconComponent = useMemo(
    () => (pckg?.locked_internal ? <Lock divider /> : undefined),
    [pckg?.locked_internal]
  );

  if (pckgLoading || !pckg || !packageId) return <Loader />;

  return (
    <PageHeader
      title={`${t('Details')}: ${pckg?.name}`}
      titleBadge={pckg.is_curated ? t('curated') : undefined}
      titlePrefix="OpenXSelect"
      goBack={goToListPage}
      render={renderActions}
      PreTitleIconComponent={PreTitleIconComponent}
    >
      <Tabs onChange={noop} value={DetailsTabs.DETAILS}>
        <Tab data-test="details-tab" label={t('Details')} value={DetailsTabs.DETAILS} />
      </Tabs>

      <Routes>
        <Route path="details" element={<PackageDetailsTab pckg={packageState} />} />
        <Route path="*" element={<Navigate replace to="../details" />} />
      </Routes>
    </PageHeader>
  );
};
